import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { setUser, userLogin, userRegister } from '../../redux/globals/globals.actions';
import Button from '../buttons/button.component';
import './AuthModal.styles.scss';

const AuthModal = ({ onGuestPlay, onClose }) => {
	const dispatch = useDispatch();
	const { register, handleSubmit, errors, watch } = useForm();
	const [view, setView] = useState('login'); // 'login', 'register'
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	const password = watch('password');

	const handleLogin = async (data) => {
		setLoading(true);
		setError('');

		try {
			const response = await dispatch(userLogin({ email: data.email, password: data.password }));
			const userData = response?.data?.data || response?.data;
			
			if (userData) {
				dispatch(setUser({ ...userData }));
				onClose();
			}
		} catch (err) {
			const errorMessage = err?.response?.data?.error?.message || 'Invalid email or password';
			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleRegister = async (data) => {
		setLoading(true);
		setError('');

		try {
			const response = await dispatch(userRegister({
				name: data.name,
				lastname: data.lastname || null,
				email: data.email,
				password: data.password
			}));
			
			const userData = response?.data?.data || response?.data;
			
			if (userData) {
				dispatch(setUser({ ...userData }));
				onClose();
			}
		} catch (err) {
			const errorMessage = err?.response?.data?.error?.message || 'Registration failed. Please try again.';
			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			className="auth-modal-overlay"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			onClick={onClose}
		>
			<motion.div
				className="auth-modal"
				initial={{ scale: 0.8, opacity: 0 }}
				animate={{ scale: 1, opacity: 1 }}
				exit={{ scale: 0.8, opacity: 0 }}
				transition={{ type: 'spring', stiffness: 250 }}
				onClick={(e) => e.stopPropagation()}
			>
				<AnimatePresence mode="wait">
					{view === 'login' && (
						<motion.div
							key="login"
							initial={{ x: -20, opacity: 0 }}
							animate={{ x: 0, opacity: 1 }}
							exit={{ x: 20, opacity: 0 }}
							className="auth-form"
						>
							<h2 className="auth-title">Login</h2>
							<p className="auth-subtitle">Sign in to save your progress</p>

							<form onSubmit={handleSubmit(handleLogin)} className="auth-form-content">
								<div className="form-group">
									<input
										type="email"
										name="email"
										placeholder="Email"
										className={errors.email ? 'error' : ''}
										ref={register({
											required: 'Email is required',
											pattern: {
												value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
												message: 'Invalid email address'
											}
										})}
									/>
									{errors.email && <span className="error-text">{errors.email.message}</span>}
								</div>

								<div className="form-group">
									<input
										type="password"
										name="password"
										placeholder="Password"
										className={errors.password ? 'error' : ''}
										ref={register({
											required: 'Password is required'
										})}
									/>
									{errors.password && <span className="error-text">{errors.password.message}</span>}
								</div>

								{error && <div className="auth-error">{error}</div>}

								<Button type="submit" className="cta-glow" isLoading={loading}>
									Login
								</Button>

								<div className="auth-divider">
									<span>OR</span>
								</div>

								<Button
									type="button"
									className="cta-glow secondary"
									onClick={() => setView('register')}
								>
									Create Account
								</Button>

								<Button
									type="button"
									className="cta-glow guest"
									onClick={onGuestPlay}
									disabled={loading}
								>
									Play as Guest
								</Button>
							</form>
						</motion.div>
					)}

					{view === 'register' && (
						<motion.div
							key="register"
							initial={{ x: -20, opacity: 0 }}
							animate={{ x: 0, opacity: 1 }}
							exit={{ x: 20, opacity: 0 }}
							className="auth-form"
						>
							<h2 className="auth-title">Create Account</h2>
							<p className="auth-subtitle">Sign up to save your progress across devices</p>

							<form onSubmit={handleSubmit(handleRegister)} className="auth-form-content">
								<div className="form-group">
									<input
										type="text"
										name="name"
										placeholder="First Name"
										className={errors.name ? 'error' : ''}
										ref={register({
											required: 'First name is required',
											minLength: {
												value: 1,
												message: 'First name is required'
											},
											maxLength: {
												value: 255,
												message: 'First name is too long'
											}
										})}
									/>
									{errors.name && <span className="error-text">{errors.name.message}</span>}
								</div>

								<div className="form-group">
									<input
										type="text"
										name="lastname"
										placeholder="Last Name (Optional)"
										className={errors.lastname ? 'error' : ''}
										ref={register({
											maxLength: {
												value: 255,
												message: 'Last name is too long'
											}
										})}
									/>
									{errors.lastname && <span className="error-text">{errors.lastname.message}</span>}
								</div>

								<div className="form-group">
									<input
										type="email"
										name="email"
										placeholder="Email"
										className={errors.email ? 'error' : ''}
										ref={register({
											required: 'Email is required',
											pattern: {
												value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
												message: 'Invalid email address'
											}
										})}
									/>
									{errors.email && <span className="error-text">{errors.email.message}</span>}
								</div>

								<div className="form-group">
									<input
										type="password"
										name="password"
										placeholder="Password (min 8 characters)"
										className={errors.password ? 'error' : ''}
										ref={register({
											required: 'Password is required',
											minLength: {
												value: 8,
												message: 'Password must be at least 8 characters'
											},
											pattern: {
												value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
												message: 'Password must contain uppercase, lowercase, and a number'
											}
										})}
									/>
									{errors.password && <span className="error-text">{errors.password.message}</span>}
								</div>

								<div className="form-group">
									<input
										type="password"
										name="confirmPassword"
										placeholder="Confirm Password"
										className={errors.confirmPassword ? 'error' : ''}
										ref={register({
											required: 'Please confirm your password',
											validate: value => value === password || 'Passwords do not match'
										})}
									/>
									{errors.confirmPassword && <span className="error-text">{errors.confirmPassword.message}</span>}
								</div>

								{error && <div className="auth-error">{error}</div>}

								<Button type="submit" className="cta-glow" isLoading={loading}>
									Create Account
								</Button>

								<div className="auth-divider">
									<span>OR</span>
								</div>

								<Button
									type="button"
									className="cta-glow secondary"
									onClick={() => {
										setView('login');
										setError('');
									}}
									disabled={loading}
								>
									Already have an account? Login
								</Button>

								<Button
									type="button"
									className="cta-glow guest"
									onClick={onGuestPlay}
									disabled={loading}
								>
									Play as Guest
								</Button>
							</form>
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>
		</motion.div>
	);
};

export default AuthModal;

